import React from "react";
import ConfirmModal from "../components/ConfirmModal";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { getCurrentUser } from "../utils/auth";
import { getUserAccessInfo } from "../utils/accessFilters";
import { buildAccessHeaders } from "../utils/accessHeaders";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const API_PATH = "/api/admin/ship-points";

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const ensureIds = (rows = []) => rows.map((row) => (row && row._id ? row : { _id: genId(), ...row }));

const emptyRow = () => ({
  _id: genId(),
  cust_code: "",
  whse_code: "",
  wm_ship_point: "",
  note: "",
  is_active: true,
});

function rowFingerprint(row) {
  return JSON.stringify({
    cust_code: String(row?.cust_code || "").trim().toUpperCase(),
    whse_code: String(row?.whse_code || "").trim().toUpperCase(),
    wm_ship_point: String(row?.wm_ship_point || "").trim(),
    note: String(row?.note || "").trim(),
    is_active: Boolean(row?.is_active),
  });
}

export default function ShipPointManagement() {
  const [rows, setRows] = React.useState([]);
  const [originalRows, setOriginalRows] = React.useState([]);
  const [customerOptions, setCustomerOptions] = React.useState([]);
  const [warehouseOptions, setWarehouseOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [isDeleteOpen, setDeleteOpen] = React.useState(false);

  const me = getCurrentUser();
  const meEmail = me?.email || "";
  const meRole = me?.role || "";
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = React.useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const requestHeaders = React.useMemo(
    () => buildAccessHeaders({ email: meEmail, role: meRole }, accessInfo, { includeCust: true, includeWhs: true }),
    [meEmail, meRole, accessInfo]
  );
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "admin-ship-points");
  const canEdit = permissionLevel === "Full" || permissionLevel === "Limited";

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [mappingResp, customersResp, warehousesResp] = await Promise.all([
        fetch(`${API_BASE}${API_PATH}?include_inactive=1`, { headers: requestHeaders }),
        fetch(`${API_BASE}/customers`, { headers: requestHeaders }),
        fetch(`${API_BASE}/warehouses`, { headers: requestHeaders }),
      ]);
      const [mappingBody, customersBody, warehousesBody] = await Promise.all([
        mappingResp.json().catch(() => ({})),
        customersResp.json().catch(() => ({})),
        warehousesResp.json().catch(() => ({})),
      ]);
      if (!mappingResp.ok || mappingBody?.ok !== true) {
        throw new Error(mappingBody?.message || mappingBody?.error || "Unable to load Ship Point mappings.");
      }
      if (!customersResp.ok || customersBody?.ok !== true) {
        throw new Error(customersBody?.message || customersBody?.error || "Unable to load customers.");
      }
      if (!warehousesResp.ok || warehousesBody?.ok !== true) {
        throw new Error(warehousesBody?.message || warehousesBody?.error || "Unable to load warehouses.");
      }

      const mappingRows = ensureIds(
        (mappingBody.rows || []).map((row) => ({
          note: "",
          is_active: true,
          ...row,
        }))
      );
      setRows(mappingRows);
      setOriginalRows(mappingRows.map((row) => ({ ...row })));
      setSelectedIds(new Set());

      const nextCustomerOptions = (customersBody.rows || [])
        .filter((row) => row?.cust_code)
        .map((row) => ({
          value: String(row.cust_code || "").trim().toUpperCase(),
          label: row.cust_name
            ? `${String(row.cust_code || "").trim().toUpperCase()} - ${String(row.cust_name || "").trim()}`
            : String(row.cust_code || "").trim().toUpperCase(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setCustomerOptions(nextCustomerOptions);

      const nextWarehouseOptions = (warehousesBody.rows || [])
        .filter((row) => row?.whse_code)
        .map((row) => ({
          value: String(row.whse_code || "").trim().toUpperCase(),
          label: row.whse_name
            ? `${String(row.whse_code || "").trim().toUpperCase()} - ${String(row.whse_name || "").trim()}`
            : String(row.whse_code || "").trim().toUpperCase(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setWarehouseOptions(nextWarehouseOptions);
    } catch (loadErr) {
      console.error(loadErr);
      setErr(loadErr?.message || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, [requestHeaders]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const dupKeySet = React.useMemo(() => {
    const seen = new Map();
    rows.forEach((row) => {
      const cust = String(row?.cust_code || "").trim().toUpperCase();
      const whse = String(row?.whse_code || "").trim().toUpperCase();
      if (!cust || !whse) return;
      const key = `${cust}__${whse}`;
      seen.set(key, (seen.get(key) || 0) + 1);
    });
    return new Set([...seen.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  }, [rows]);

  const toggleSelect = (rowId, checked) => {
    setSelectedIds((prev) => {
      if (!canEdit) return prev;
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const onAddRow = () => {
    if (!canEdit) return;
    setRows((prev) => [emptyRow(), ...prev]);
    setNotice("");
    setErr("");
  };

  const askDelete = () => {
    if (!canEdit || selectedIds.size === 0) return;
    setDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!canEdit) return;
    setRows((prev) => prev.filter((row) => !selectedIds.has(row._id)));
    setSelectedIds(new Set());
    setDeleteOpen(false);
    setNotice("");
    setErr("");
  };

  const onCellChange = (index, key, value) => {
    setRows((prev) => {
      if (!canEdit) return prev;
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const onSave = async () => {
    if (!canEdit) {
      setErr("You do not have permission to edit Ship Point Management.");
      return;
    }
    setErr("");
    setNotice("");

    for (const row of rows) {
      if (!String(row?.cust_code || "").trim()) {
        setErr("Cust Code is required.");
        return;
      }
      if (!String(row?.whse_code || "").trim()) {
        setErr("WHSE Code is required.");
        return;
      }
      if (!String(row?.wm_ship_point || "").trim()) {
        setErr("WM Ship Point is required.");
        return;
      }
    }
    if (dupKeySet.size > 0) {
      setErr("Duplicate Cust Code + WHSE Code pairs are not allowed.");
      return;
    }

    setSaving(true);
    try {
      const headers = { ...requestHeaders, "Content-Type": "application/json" };
      const originalById = new Map(originalRows.filter((row) => row.id).map((row) => [row.id, row]));
      const currentIds = new Set(rows.filter((row) => row.id).map((row) => row.id));
      const deletedIds = originalRows.filter((row) => row.id && !currentIds.has(row.id)).map((row) => row.id);

      for (const deletedId of deletedIds) {
        const resp = await fetch(`${API_BASE}${API_PATH}/${deletedId}`, { method: "DELETE", headers });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.message || payload?.error || "Unable to delete Ship Point mapping.");
        }
      }

      for (const row of rows) {
        const body = {
          cust_code: String(row.cust_code || "").trim().toUpperCase(),
          whse_code: String(row.whse_code || "").trim().toUpperCase(),
          wm_ship_point: String(row.wm_ship_point || "").trim(),
          note: String(row.note || "").trim(),
          is_active: Boolean(row.is_active),
        };
        if (!row.id) {
          const resp = await fetch(`${API_BASE}${API_PATH}`, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
          });
          const payload = await resp.json().catch(() => ({}));
          if (!resp.ok || payload?.ok !== true) {
            throw new Error(payload?.message || payload?.error || "Unable to create Ship Point mapping.");
          }
          continue;
        }

        const original = originalById.get(row.id);
        if (original && rowFingerprint(original) === rowFingerprint(row)) continue;

        const resp = await fetch(`${API_BASE}${API_PATH}/${row.id}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(body),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.message || payload?.error || "Unable to update Ship Point mapping.");
        }
      }

      setNotice("Ship Point mappings saved.");
      await loadAll();
    } catch (saveErr) {
      console.error(saveErr);
      setErr(saveErr?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-2 flex items-center gap-2">
        <h1 className="text-xl font-bold">Ship Point Management</h1>
        <UserAccessSummary className="ml-2" />
        {!canEdit && (
          <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
            Read only
          </span>
        )}
        <button className="rounded border bg-gray-100 px-3 py-1 disabled:opacity-50" onClick={onAddRow} disabled={!canEdit}>
          + Add Row
        </button>
        <button
          className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50"
          onClick={onSave}
          disabled={saving || loading || !canEdit}
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={askDelete}
          disabled={!canEdit || selectedIds.size === 0}
          className={
            !canEdit || selectedIds.size === 0
              ? "rounded bg-gray-200 px-3 py-1 text-gray-500 cursor-not-allowed"
              : "rounded bg-red-600 px-3 py-1 text-white hover:bg-red-700"
          }
        >
          Delete
        </button>
        {loading && <span className="text-xs text-slate-500">Loading...</span>}
        {notice && <span className="ml-2 text-green-700">{notice}</span>}
        {err && <span className="ml-2 text-red-600">{err}</span>}
      </div>

      <div className="overflow-auto rounded border">
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th style={{ width: 36, minWidth: 36 }} className="sticky top-0 bg-white border-b px-2 py-2">
                &nbsp;
              </th>
              <th className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold">Cust Code*</th>
              <th className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold">WHSE Code*</th>
              <th className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold">WM Ship Point*</th>
              <th className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold">Description / Note</th>
              <th className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold">Active</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-500">
                  No Ship Point mappings are configured.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const dupKey = `${String(row?.cust_code || "").trim().toUpperCase()}__${String(row?.whse_code || "")
                  .trim()
                  .toUpperCase()}`;
                const isDup = dupKeySet.has(dupKey);
                return (
                  <tr key={row._id} className="border-b last:border-b-0">
                    <td className="px-2 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row._id)}
                        onChange={(event) => toggleSelect(row._id, event.target.checked)}
                        disabled={!canEdit}
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <select
                        value={row.cust_code || ""}
                        onChange={(event) => onCellChange(index, "cust_code", event.target.value)}
                        disabled={!canEdit}
                        className={`w-full rounded border px-2 py-1 text-sm ${isDup ? "border-red-500" : "border-slate-300"}`}
                      >
                        <option value="">Select Cust Code</option>
                        {customerOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <select
                        value={row.whse_code || ""}
                        onChange={(event) => onCellChange(index, "whse_code", event.target.value)}
                        disabled={!canEdit}
                        className={`w-full rounded border px-2 py-1 text-sm ${isDup ? "border-red-500" : "border-slate-300"}`}
                      >
                        <option value="">Select WHSE Code</option>
                        {warehouseOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <input
                        type="text"
                        value={row.wm_ship_point || ""}
                        onChange={(event) => onCellChange(index, "wm_ship_point", event.target.value)}
                        disabled={!canEdit}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                        placeholder="WM Ship Point"
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <input
                        type="text"
                        value={row.note || ""}
                        onChange={(event) => onCellChange(index, "note", event.target.value)}
                        disabled={!canEdit}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                        placeholder="Description / Note"
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(row.is_active)}
                          onChange={(event) => onCellChange(index, "is_active", event.target.checked)}
                          disabled={!canEdit}
                        />
                        {row.is_active ? "Active" : "Inactive"}
                      </label>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={isDeleteOpen}
        title="Delete Ship Point Mappings"
        message={`Delete ${selectedIds.size} selected row(s)? Changes will be saved when you click Save.`}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        confirmText="Delete"
      />
    </div>
  );
}
